import ItemBuilderView, { ItemBuilderAction, ItemBuilderViewState, ItemBuilderViewType } from './ItemBuilderView';
import ItemBuilderArgs from './ItemBuilderArgs';
import Modal from '../Modal';
import ItemView from '../ItemView';
import ItemMouseEvent from '../ItemMouseEvent';
import FolderView from '../FolderView';
import JQuery from 'jquery';
import MouseDownEvent = JQuery.MouseDownEvent;
import MouseUpEvent = JQuery.MouseUpEvent;
import ClickEvent = JQuery.ClickEvent;
import MouseMoveEvent = JQuery.MouseMoveEvent;

declare type JQueryEvent = MouseDownEvent | MouseUpEvent | ClickEvent | MouseMoveEvent | any;

abstract class AbstractItemBuilderView implements ItemBuilderView {
  protected el: string;
  protected viewState: ItemBuilderViewState;
  protected model: Modal;

  private onChangeFunction: ((modal: Modal) => void | undefined) | undefined;

  private lastTarget: Modal | undefined;
  private lastCurrent: Modal | undefined;

  private positionX = 0;
  private positionY = 0;

  protected folderViews: ItemView[];
  protected listViews: ItemView[];
  protected breadCrumbViews: ItemView[];

  protected constructor(args: ItemBuilderArgs) {
    this.el = `#${args.el}`;
    this.model = args.model;
    this.viewState = {
      action: ItemBuilderAction.FREE,
    };

    this.folderViews = [];
    this.listViews = [];
    this.breadCrumbViews = [];
  }

  public initialize(): void {
    this.viewState = { action: ItemBuilderAction.FREE };
    this.loadModel(this.model);
    this.initializeListeners();
  }

  public getListViews = (): ItemView[] => this.listViews;

  public getFolderViews = (): ItemView[] => this.folderViews;

  public getBreadCrumbViews = (): ItemView[] => this.breadCrumbViews;

  public getLastTarget = (): Modal | undefined => this.lastTarget;

  public setLastTarget = (item: Modal) => (this.lastTarget = item);

  public getLastCurrent = (): Modal | undefined => this.lastCurrent;

  public setLastCurrent = (item: Modal) => (this.lastCurrent = item);

  public getPositionX = () => this.positionX;

  public setPositionX = (x: number) => (this.positionX = x);

  public getPositionY = () => this.positionY;

  public setPositionY = (y: number) => (this.positionY = y);

  public resetLastUpdatedView = () => {
    this.lastCurrent = undefined;
    this.lastTarget = undefined;
  };

  public setState(state: ItemBuilderViewState): void {
    this.viewState = state;
  }

  public getState(): ItemBuilderViewState {
    return this.viewState;
  }

  public onChange(func: (modal: Modal) => void): void {
    this.onChangeFunction = func;
  }

  public rerender(): void {
    this.onChangeFunction?.(this.model);
  }

  public terminate(): void {
    this.terminateListeners();
  }

  public getModel(): Modal {
    return this.model;
  }

  private initializeListeners() {
    $(document)
      .on('mousedown', this.el, (event: MouseDownEvent) => this.handleMouseDown(event))
      .on('mousemove', this.el, (event: MouseMoveEvent) => this.handleMouseMove(event))
      .on('mouseup', this.el, (event: MouseUpEvent) => this.handleMouseUp(event));

    $(document).on('click', (event: ClickEvent) => this.handleOutsideClick(event));
  }

  private terminateListeners() {
    $(document).off('mousedown').off('mousemove').off('mouseup').off('click');
    this.folderViews.forEach((f) => f.terminate());
    this.breadCrumbViews.forEach((f) => f.terminate());
    this.listViews.forEach((f) => f.terminate());
  }

  private handleOutsideClick(event: JQuery.ClickEvent) {
    const flowMouseEvent: ItemMouseEvent = this.buildEvent(event);
    if (flowMouseEvent.component == undefined) {
      $(document).triggerHandler('folder:clear-selected-folders');
    }
  }

  private handleMouseDown(event: JQuery.MouseDownEvent) {
    if (event.which != 1) return;
    const flowMouseEvent: ItemMouseEvent = this.buildEvent(event);
    this.pointerDown(flowMouseEvent);
  }

  private handleMouseUp(event: JQuery.MouseUpEvent) {
    const flowMouseEvent: ItemMouseEvent = this.buildEvent(event);
    this.pointerUp(flowMouseEvent);
  }

  private handleMouseMove(event: JQuery.MouseMoveEvent) {
    event.preventDefault();
    const flowMouseEvent: ItemMouseEvent = this.buildEvent(event);
    this.mousemove(flowMouseEvent);
  }

  private buildEvent(event: JQueryEvent): ItemMouseEvent {
    return {
      position: {
        x: event.pageX - 95,
        y: event.pageY - 60,
      },
      component: this.getComponent(event.target),
    };
  }

  private getComponent(document: any): ItemView | undefined {
    return this.getComponentByDocument(document);
  }

  private getComponentByDocument(document: any): ItemView | undefined {
    const prefixes = ['folder', 'list', 'breadcrumb'];
    let doc: any = $(document)
      .closest(prefixes.map((p) => `[id^='${p}_']`).join(','))
      .get()[0];
    if (doc == undefined) {
      doc = $(document)
        .closest(prefixes.map((p) => `[class^='${p}_']`).join(','))
        .get()[0];
      if (doc) {
        return this.getComponentByView(doc.className.split(' ')[0]);
      }
    }
    return doc == undefined ? undefined : this.getComponentByView(doc.id);
  }

  private getComponentByView(id: string) {
    const [type, name] = id.split('_');
    let views: ItemView[];
    switch (type) {
      case 'folder':
        views = this.folderViews;
        break;
      case 'list':
        views = this.listViews;
        break;
      case 'breadcrumb':
        views = this.breadCrumbViews;
        break;
      default:
        throw new Error('UnsupportedType');
    }
    return views.find((v) => v.getModel().id == name);
  }

  private loadModel(model: Modal) {
    const item = model.value;
    switch (item?.type) {
      case 'Folder':
        const folderView = new FolderView({
          type: ItemBuilderViewType.FOLDER,
          model: model,
          el: `#folder_${model.id}`,
          parentEl: `#parent_folder_${model.id}`,
          view: this,
        });
        folderView.initialize();
        this.folderViews.push(folderView);
        break;
      case 'List':
        const listView = new FolderView({
          type: ItemBuilderViewType.CARD,
          model: model,
          el: `#list_${model.id}`,
          parentEl: `#parent_list_${model.id}`,
          view: this,
        });
        listView.initialize();
        this.listViews.push(listView);
        break;
      case 'Breadcrumb':
        const breadCrumbView = new FolderView({
          type: ItemBuilderViewType.BREADCRUMB,
          model: model,
          el: `#breadcrumb_${model.id}`,
          parentEl: `#parent_breadcrumb_${model.id}`,
          view: this,
        });
        breadCrumbView.initialize();
        this.breadCrumbViews.push(breadCrumbView);
        break;
    }
    model.children?.forEach((c) => this.loadModel(c));
  }

  protected abstract pointerUp(event: ItemMouseEvent): void;

  protected abstract pointerDown(event: ItemMouseEvent): void;

  protected abstract mousemove(event: ItemMouseEvent): void;

  protected abstract insert(current: ItemView, target: ItemView): void;
}

export default AbstractItemBuilderView;
